import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  IconButton,
  InputAdornment,
  OutlinedInput,
  Typography
} from "@mui/material";
import { wait } from "@testing-library/user-event/dist/utils";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import logo from "../../assets/logo_tunnar2.png";
import wallpaper from "../../assets/wallpaper.png";
import { useAuth } from "../../auth/AuthContext";
import { useUser } from "../../auth/UserContext";
import { setStoredUser } from "../../auth/session";
import FirstAccessModal from "../../components/modal/FirstAccessModal";
import ForgotPasswordModal, {
  ForgotResult
} from "../../components/modal/ForgotPasswordModal";

import { FaCode } from "react-icons/fa";
import { makeToasterErrorHandler } from "../../common/http/makeToasterErrorHandler";
import PageToaster from "../../components/toaster/PageToaster";
import { usePageToast } from "../../hooks/usePageToast";
import api from "../../services/api";
import {
  Aboutme,
  Button,
  Container,
  Footer,
  GlobalStyle,
  LoginForm,
  Logo,
  VerseText
} from "./Login.styles";
import { verses } from "./versiculos";

const Login: React.FC = () => {
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const { login } = useAuth();
  const { setUser } = useUser(); // Obtém o método para atualizar o usuário

  const [isHovered, setIsHovered] = useState(false);

  const [forgotModalOpen, setForgotModalOpen] = useState(false);
  const [firstAccessModalOpen, setFirstAccressModalOpen] = useState(false);

  const [showPassword, setShowPassword] = useState(false);
  const [selectedStep, setSelectedStep] = useState(1);
  const [processType, setProcessType] = useState<any>(null);

  const [currentIndex, setCurrentIndex] = useState(0);

  const inputSx = {
    mb: 2,
    borderRadius: "14px",
    color: "#eaf2ff",
    background: "rgba(8, 18, 42, 0.55)",
    backdropFilter: "blur(6px)",
    transition: "box-shadow .25s ease",
    "& input": { padding: "15px 14px" },
    "& input::placeholder": { color: "rgba(165, 195, 240, 0.6)", opacity: 1 },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(43, 123, 255, 0.32)"
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(43, 123, 255, 0.6)"
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#2b7bff"
    },
    "&.Mui-focused": {
      boxShadow:
        "0 0 0 3px rgba(43, 123, 255, 0.25), 0 0 18px rgba(34, 211, 238, 0.2)"
    },
    "& .MuiSvgIcon-root": { color: "rgba(170, 200, 240, 0.85)" }
  } as const;

  const containerId = "toaster-login-page";
  const { autoSuccess, autoError, autoInfo, autoWarn } = usePageToast(
    containerId,
    true
  );

  const handleHttpError = makeToasterErrorHandler(autoError);

  const handleLogin = async () => {
    if (!email || !password) {
      alert("Por favor, preencha todos os campos");
      return;
    }

    setLoading(true);

    try {
      const response = await api.post("/auth/login", {
        email,
        password
      });

      // console.log('Initial: ', response.data);

      const { user_id, access_token, name, profile, avatar, company_id } =
        response.data;

      // console.log('Avatar', avatar);

      setStoredUser({ user_id, name, profile, avatar, company_id });

      login(access_token); // Armazena o token no AuthContext
      setUser({ user_id, name, profile, avatar, company_id }); // Atualiza o usuário no contexto

      autoSuccess(`Login realizado com sucesso!`);
      wait(2000).then(() => {
        setPassword("");
        setEmail("");
        navigate("/welcome");
      });
    } catch (error: any) {
      if (error.response) {
        if (
          error.response.status === 401 &&
          error.response.data.message === "Primeiro acesso não autorizado!"
        ) {
          autoInfo("Bem vindo ao Thunnar! Redefina sua senha para continuar.");

          handleFirstAccess();
        } else {
          handleHttpError(error);
        }
      } else {
        console.log("error", error);
        handleHttpError(error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSelectedStep = (step: number, process: any) => {
    setSelectedStep(step);
    setProcessType(process);
    setForgotModalOpen(true);
  };

  const handleFirstAccess = () => {
    setFirstAccressModalOpen(true);
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % verses.length);
    }, 30000); // troca a cada 10 segundos

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    setPassword("");
  }, [firstAccessModalOpen]);

  const setNewPassword = (data: ForgotResult) => {
    console.log("data", data);
    setTimeout(() => {
      setPassword(data.newPassword || "");
    }, 2000);
  };

  return (
    <>
      <PageToaster containerId={containerId} />
      <GlobalStyle />

      <div
        style={{ position: "absolute", bottom: 80, right: 50, zIndex: 1000 }}
      >
        <Aboutme onClick={() => navigate("/aboutme")}>
          <FaCode size={60} color="white" />
          Sobre mim
        </Aboutme>
      </div>
      <Container $bgImage={wallpaper}>
        {/* Partículas ao fundo */}

        {/* Login form com hover na logo */}
        <LoginForm
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <Logo src={logo} alt="Logo" />
          <br />

          <OutlinedInput
            type={"email"}
            placeholder="E-mail"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleLogin()}
            fullWidth
            sx={inputSx}
          />

          <OutlinedInput
            type={showPassword ? "text" : "password"}
            placeholder="Senha"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleLogin()}
            fullWidth
            sx={inputSx}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword((prev) => !prev)}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            }
          />

          <Typography
            variant="body2"
            fontWeight={600}
            sx={{
              cursor: "pointer",
              mt: 0.5,
              mb: 2.5,
              alignSelf: "flex-end",
              color: "rgba(150, 190, 255, 0.9)",
              transition: "color .2s ease",
              "&:hover": { color: "#22d3ee" }
            }}
            onClick={() => handleSelectedStep(1, null)}
          >
            Esqueceu a senha?
          </Typography>

          <Button onClick={handleLogin} disabled={loading}>
            {loading ? "Entrando..." : "Entrar"}
          </Button>
        </LoginForm>

        <ForgotPasswordModal
          open={forgotModalOpen}
          onClose={() => setForgotModalOpen(false)}
          initialStep={selectedStep}
          process={processType}
          onComplete={setNewPassword}
          currentEmail={email}
        />

        <FirstAccessModal
          open={firstAccessModalOpen}
          onClose={() => setFirstAccressModalOpen(false)}
          onComplete={setNewPassword}
          currentEmail={email}
        />

        <Footer>
          <VerseText key={currentIndex}>{verses[currentIndex]}</VerseText>
        </Footer>
      </Container>
    </>
  );
};

export default Login;
